/**
 * Passage-destination marker. Drawn as a MapLibre DOM Marker rather than a GeoJSON
 * symbol layer on purpose: a single, rarely-changing point fed through a long-lived
 * GeoJSON source can have its worker update dropped (the source's main-thread data
 * updates but the worker never re-tiles, so nothing renders). A DOM Marker sits on
 * top of the canvas and is immune to that whole tiling/worker pipeline, so the
 * destination always shows the moment it is set.
 *
 * The look is deliberately NOT the thin charted-anchorage glyph: a filled navy disc
 * with a white anchor, so a destination never reads as a charted anchorage.
 */
import { Marker } from 'maplibre-gl';
import type { Map as MaplibreMap } from 'maplibre-gl';

export interface DestinationMark {
  position: [number, number];
  name?: string;
}

// The marker is stored on the map instance, so each map owns its own and there is
// no stale cross-instance reference to manage.
interface MarkerHolder {
  __destinationMarker?: Marker | null;
  __destinationOnSelect?: (() => void) | null;
}

const ANCHOR_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
  '<circle cx="12" cy="5" r="3"/><line x1="12" y1="22" x2="12" y2="8"/>' +
  '<path d="M5 12H2a10 10 0 0 0 20 0h-3"/></svg>';

function buildElement(name: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'acp-destination-marker';
  el.style.cssText = 'display:flex;flex-direction:column;align-items:center;pointer-events:none;';
  el.innerHTML =
    '<div class="acp-destination-disc" style="width:30px;height:30px;border-radius:50%;background:#1f4e79;' +
    'border:2px solid rgba(255,255,255,0.95);box-shadow:0 1px 3px rgba(0,0,0,0.4);' +
    'display:flex;align-items:center;justify-content:center;pointer-events:auto;cursor:pointer;">' +
    ANCHOR_SVG +
    '</div>' +
    '<div class="acp-destination-label" style="margin-top:2px;' +
    'font:600 11px system-ui,-apple-system,sans-serif;color:#1f4e79;' +
    'text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;white-space:nowrap;">' +
    '</div>';
  // The name goes in via textContent, not string-built HTML, so a destination
  // name containing markup renders as plain text instead of becoming elements.
  const label = el.querySelector('.acp-destination-label');
  if (label) label.textContent = name;
  return el;
}

/** Create, move, or remove the destination marker to match `destination`. */
export function setDestinationMarker(
  map: MaplibreMap,
  destination: DestinationMark | null,
  onSelect?: () => void,
): void {
  const holder = map as unknown as MarkerHolder;
  holder.__destinationOnSelect = onSelect ?? null;
  if (!destination) {
    if (holder.__destinationMarker) {
      holder.__destinationMarker.remove();
      holder.__destinationMarker = null;
    }
    return;
  }
  const name = destination.name ?? 'Destination';
  if (!holder.__destinationMarker) {
    const element = buildElement(name);
    // Tapping the disc selects the destination (opens its sheet, which offers
    // Clear destination). stopPropagation so the tap doesn't also fall through to
    // the map as an open-water chart tap. The label stays pointer-events:none.
    const disc = element.querySelector('.acp-destination-disc');
    if (disc) {
      disc.addEventListener('click', (e) => {
        e.stopPropagation();
        (map as unknown as MarkerHolder).__destinationOnSelect?.();
      });
    }
    holder.__destinationMarker = new Marker({ element, anchor: 'center' })
      .setLngLat(destination.position)
      .addTo(map);
  } else {
    holder.__destinationMarker.setLngLat(destination.position);
    const label = holder.__destinationMarker.getElement().querySelector('.acp-destination-label');
    if (label) label.textContent = name;
  }
}
